// frontend/src/features/assignments/assignmentsSlice.js
/**
 * Assignments slice (Phase 5 -> Phase 7 update).
 *
 * Provides a simple, read-only Redux store for assignment-based driver lists.
 * Loads assignment rows for a given site using canonical assignment data,
 * scoped by company and existing UI filters (agency, search, source).
 *
 * Phase 7 change:
 * - Stop hardcoding MAIN site
 * - Use numeric siteId
 * - Default to sitesSlice.selectedSiteId when siteId not provided
 */
import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import {
  fetchSiteAssignments,
  fetchCompanyAssignments,
} from '../../services/assignmentService';
import {
  selectConcreteSelectedSiteId,
  selectIsAllSitesMode,
} from '../sites/sitesSlice';

/**
 * Async thunk: load assignments for a site.
 *
 * - Uses selected site context from sitesSlice by default
 * - Reads existing UI filters (agency + search + source) from Redux
 * - Returns a flat list of assignment rows
 */
export const loadSiteAssignments = createAsyncThunk(
  'assignments/loadSiteAssignments',
  async (
    { siteId, statuses = ['active'], timeBucket } = {},
    { getState, rejectWithValue }
  ) => {
    const state = getState();

    // 1. Read shared UI filters
    const agencyId = state.ui?.filters?.agencyId || '';
    const search = state.ui?.filters?.q || '';
    const source = state.ui?.filters?.source || 'all';

    // 2. Read protective site states
    const isAllSitesMode = selectIsAllSitesMode(state);
    const selectedConcreteSiteId = selectConcreteSelectedSiteId(state);

    // 3. Check for explicit overrides (e.g., component passed a specific ID)
    const explicitSiteId =
      siteId != null && Number.isFinite(Number(siteId)) && Number(siteId) > 0
        ? Number(siteId)
        : null;

    const finalSiteId = explicitSiteId ?? selectedConcreteSiteId;

    // ROUTE A: Component explicitly demanded a specific site
    if (explicitSiteId != null) {
      return await fetchSiteAssignments(finalSiteId, {
        statuses, agencyId, search, source, timeBucket,
      });
    }

    // ROUTE B: User is in global "All Sites" mode
    if (isAllSitesMode) {
      return await fetchCompanyAssignments({
        statuses, agencyId, search, source, timeBucket,
      });
    }

    // ROUTE C: Standard site-scoped mode (fallback safe check)
    if (!Number.isFinite(finalSiteId) || finalSiteId <= 0) {
      return rejectWithValue('No site selected');
    }

    return await fetchSiteAssignments(finalSiteId, {
      statuses, agencyId, search, source, timeBucket,
    });
  }
);

const assignmentsSlice = createSlice({
  name: 'assignments',

  // Keep state intentionally small
  initialState: {
    rows: [],        // assignment rows returned from backend
    loading: false,  // async state for UI
    error: null,     // load error, if any
    lastLoaded: null // timestamp of last successful load
  },

  reducers: {
    /**
     * Replace all assignment rows.
     * Useful for full refreshes or manual overrides.
     */
    setAssignments(state, action) {
      state.rows = Array.isArray(action.payload) ? action.payload : [];
    },

    /**
     * Patch a single assignment row in-place.
     * Intended for real-time updates (e.g. Socket.IO events).
     */
    patchAssignment(state, action) {
      const { assignment_id, patch } = action.payload || {};
      if (!assignment_id) return;

      const idx = state.rows.findIndex(
        (r) => Number(r.assignment_id) === Number(assignment_id)
      );

      if (idx >= 0) {
        state.rows[idx] = {
          ...state.rows[idx],
          ...(patch || {}),
        };
      }
    },
  },

  extraReducers(builder) {
    builder
      // Request started
      .addCase(loadSiteAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      // Request succeeded
      .addCase(loadSiteAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload || [];
        state.lastLoaded = Date.now();
      })

      // Request failed
      .addCase(loadSiteAssignments.rejected, (state, action) => {
        state.loading = false;
        // ✅ supports rejectWithValue('No site selected')
        state.error =
          action.payload || action.error?.message || 'Failed to load assignments';
      });
  },
});

export const { setAssignments, patchAssignment } = assignmentsSlice.actions;
export default assignmentsSlice.reducer;

/* =========================
   Base selectors
   ========================= */

export const selectAssignmentRows = (s) => s.assignments?.rows || [];
export const selectAssignmentsLoading = (s) => !!s.assignments?.loading;
export const selectAssignmentsError = (s) => s.assignments?.error || null;

/**
 * Selector factory for tab-based views.
 *
 * Filters assignments by assignment_status (e.g. 'active')
 * Applies a client-side safety filter for search text
 */
export const makeSelectAssignmentsForTab = (statusLower /* 'active' */) =>
  createSelector(
    [selectAssignmentRows, (state) => state.ui?.filters || {}],
    (rows, { q }) => {
      const query = (q || '').trim().toLowerCase();

      return rows.filter((r) => {
        // assignment_status is the operational truth for assignment-driven screens
        const rowStatus = r.assignment_status;

        // Match assignment status for the tab
        if (
          String(rowStatus).toLowerCase() !==
          String(statusLower).toLowerCase()
        ) {
          return false;
        }

        // Optional client-side search safety filter
        if (query) {
          // ✅ include vrn fallback (matches other lists)
          const hay = `${r.name || ''} ${r.email || ''} ${
            r.vehicle_registration_number || r.vrn || ''
          }`.toLowerCase();
          if (!hay.includes(query)) return false;
        }

        return true;
      });
    }
  );
